// Image optimization helpers (Module 25 — bandwidth-aware mobile pass).
//
// Two layers, one durable utility:
//
//   1. RENDER-TIME: build Supabase signed URLs that include width/quality
//      transform params. Supabase Storage applies these server-side, so
//      we send only the bytes a phone needs (often ~5–10% of the original).
//      If image transforms are unavailable on the project (some self-host
//      or downgraded plans don't ship them), we degrade to the un-transformed
//      signed URL — callers should also wire `onError` for the bytes-on-the-
//      wire fallback path.
//
//   2. UPLOAD-TIME: best-effort client-side downscale before the bytes
//      ever leave the device. Caps at 1600px on the longest edge,
//      JPEG q=0.85. Keeps HEIC/unknown formats untouched (Safari uploads
//      HEIC fine; we don't try to transcode here). All failures fall
//      through to the original file so we never block an upload.
//
// Why one module: every image surface (child photos, message attachments,
// moderation thumbnails) already routes through Supabase Storage signed
// URLs. Sharing the transform logic keeps cache invariants and `srcSet`
// behaviour consistent regardless of bucket.

import { supabase, isSupabaseConfigured } from "./supabase";

const DEFAULT_TTL = 60 * 60; // 1h, matches the rest of the app.

/**
 * Cache: `${bucket}|${path}|${optsKey}` → { url, expiresAt }.
 * Trimmed lazily — entries past expiry are simply re-fetched.
 */
const urlCache = new Map();

function optsKey(opts) {
  if (!opts) return "raw";
  const { width = "", height = "", quality = "", resize = "" } = opts;
  return `w${width}h${height}q${quality}r${resize}`;
}

/**
 * Resolve a transformed Supabase signed URL. `opts` is optional —
 * omit it for the original bytes. On transform failure, falls back
 * to the un-transformed signed URL once and caches that result so
 * we don't hammer the API.
 */
export async function transformedSignedUrl(bucket, path, opts, { ttl = DEFAULT_TTL } = {}) {
  if (!isSupabaseConfigured || !path) return null;
  const cacheKey = `${bucket}|${path}|${optsKey(opts)}`;
  const cached = urlCache.get(cacheKey);
  if (cached && cached.expiresAt > Date.now()) return cached.url;

  const transform = opts ? trimTransform(opts) : undefined;

  const { data, error } = await supabase.storage
    .from(bucket)
    .createSignedUrl(path, ttl, transform ? { transform } : undefined);

  if (error || !data?.signedUrl) {
    // Fallback for unavailable transforms — try plain URL once.
    if (transform) {
      const plain = await supabase.storage.from(bucket).createSignedUrl(path, ttl);
      if (plain.data?.signedUrl) {
        urlCache.set(cacheKey, {
          url: plain.data.signedUrl,
          expiresAt: Date.now() + (ttl - 60) * 1000,
        });
        return plain.data.signedUrl;
      }
    }
    return null;
  }

  urlCache.set(cacheKey, {
    url: data.signedUrl,
    expiresAt: Date.now() + (ttl - 60) * 1000,
  });
  return data.signedUrl;
}

function trimTransform({ width, height, quality, resize }) {
  const t = {};
  if (width)   t.width   = width;
  if (height)  t.height  = height;
  if (quality) t.quality = quality;
  if (resize)  t.resize  = resize;
  return Object.keys(t).length ? t : undefined;
}

/**
 * Build {src, srcSet, sizes} for a responsive image. Generates one entry
 * per width at the same quality. Caller should still set `loading="lazy"`
 * and `decoding="async"` on the <img>.
 *
 *   const props = await responsiveImage("child-photos", path, {
 *     widths: [320, 480, 640],
 *     quality: 70,
 *     sizes: "(min-width: 768px) 25vw, 50vw",
 *   });
 *   <img {...props} alt="…" loading="lazy" decoding="async" />
 *
 * Returns null if the path is missing or all URLs failed to resolve.
 */
export async function responsiveImage(bucket, path, {
  widths = [480], quality = 70, resize = "cover", sizes = "100vw",
} = {}) {
  if (!path) return null;
  // Resolve all widths in parallel; the cache de-duplicates repeats.
  const entries = await Promise.all(
    widths.map(async (w) => ({
      w,
      url: await transformedSignedUrl(bucket, path, { width: w, quality, resize }),
    })),
  );
  const valid = entries.filter((e) => e.url);
  if (valid.length === 0) return null;
  const srcSet = valid.map(({ url, w }) => `${url} ${w}w`).join(", ");
  const src = valid[Math.floor(valid.length / 2)].url;
  return { src, srcSet, sizes };
}

// ---------------------------------------------------------------------------
// Upload-time downscale
// ---------------------------------------------------------------------------

const DOWNSCALE_CANDIDATES = new Set([
  "image/jpeg", "image/jpg", "image/png", "image/webp",
]);

/**
 * Pre-flight a file before upload. If it's a sufficiently large jpeg /
 * png / webp, draws it into a clamped canvas and re-encodes as JPEG
 * q=0.85 — typically ~70% smaller bytes for phone photos. HEIC, GIF,
 * SVG, video, and PDFs pass through unchanged.
 *
 * Always returns a File-shaped object with `name` + `type` populated,
 * so callers can use `file.size`, `file.type`, etc. uniformly.
 *
 * Never throws — on any error, returns the original file.
 */
export async function prepareImageForUpload(file, {
  maxDim = 1600, quality = 0.85, minSizeBytes = 600 * 1024,
} = {}) {
  if (!file) return file;
  if (!DOWNSCALE_CANDIDATES.has((file.type || "").toLowerCase())) return file;
  if (file.size <= minSizeBytes) return file;
  if (typeof document === "undefined") return file;

  try {
    const bitmap = await loadBitmap(file);
    if (!bitmap) return file;
    const { width: w, height: h } = bitmap;
    if (Math.max(w, h) <= maxDim) {
      bitmap.close?.();
      return file;
    }
    const ratio = maxDim / Math.max(w, h);
    const tw = Math.round(w * ratio);
    const th = Math.round(h * ratio);

    const canvas = document.createElement("canvas");
    canvas.width = tw;
    canvas.height = th;
    const ctx = canvas.getContext("2d");
    if (!ctx) return file;
    ctx.drawImage(bitmap, 0, 0, tw, th);
    bitmap.close?.();

    const blob = await new Promise((resolve) => {
      canvas.toBlob(resolve, "image/jpeg", quality);
    });
    if (!blob || blob.size >= file.size) return file;

    // Preserve filename but normalise extension to .jpg since we re-encoded.
    const baseName = (file.name || "photo").replace(/\.(jpe?g|png|webp)$/i, "");
    return new File([blob], `${baseName}.jpg`, {
      type: "image/jpeg",
      lastModified: Date.now(),
    });
  } catch {
    return file;
  }
}

async function loadBitmap(file) {
  // createImageBitmap is fast and decodes off-thread on modern browsers.
  if (typeof createImageBitmap === "function") {
    try {
      return await createImageBitmap(file);
    } catch {
      /* fall through */
    }
  }
  // Fallback: <img src=blob:url> — works on older Safari / Firefox.
  return await new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      resolve(img);
    };
    img.onerror = (err) => {
      URL.revokeObjectURL(url);
      reject(err);
    };
    img.src = url;
  });
}

// ---------------------------------------------------------------------------
// Public-URL transforms (used by CMS markdown renderer)
//
// CMS images live in the public `cms-media` bucket and are referenced by
// permanent URLs like:
//   https://<proj>.supabase.co/storage/v1/object/public/cms-media/<path>
//
// Supabase exposes the same object through the image-transformation
// endpoint at:
//   https://<proj>.supabase.co/storage/v1/render/image/public/cms-media/<path>?width=…&quality=…
//
// `publicTransformedUrl` is a pure-string mapping: no extra round trip,
// no bucket lookup. If the input doesn't match the public-URL shape, we
// return null so the caller can fall back to the original URL.
// ---------------------------------------------------------------------------

const PUBLIC_OBJECT_RE = /\/storage\/v1\/object\/public\//;

export function publicTransformedUrl(url, { width, quality, resize } = {}) {
  if (!url || typeof url !== "string") return null;
  if (!PUBLIC_OBJECT_RE.test(url)) return null;
  const rendered = url.replace(PUBLIC_OBJECT_RE, "/storage/v1/render/image/public/");
  const params = [];
  if (width)   params.push(`width=${encodeURIComponent(width)}`);
  if (quality) params.push(`quality=${encodeURIComponent(quality)}`);
  if (resize)  params.push(`resize=${encodeURIComponent(resize)}`);
  if (params.length === 0) return rendered;
  const sep = rendered.includes("?") ? "&" : "?";
  return `${rendered}${sep}${params.join("&")}`;
}

/**
 * Build {src, srcSet} for a Supabase public-bucket URL. Returns null if
 * the URL can't be transformed (foreign host, already a render URL,
 * etc.) so callers can fall back to the original `<img>`.
 */
export function publicResponsiveSet(url, { widths = [480, 800, 1200], quality = 75 } = {}) {
  if (!publicTransformedUrl(url)) return null;
  const set = widths
    .map((w) => {
      const u = publicTransformedUrl(url, { width: w, quality });
      return u ? `${u} ${w}w` : null;
    })
    .filter(Boolean)
    .join(", ");
  if (!set) return null;
  // Pick the middle width as the default `src`.
  const mid = widths[Math.floor(widths.length / 2)];
  const src = publicTransformedUrl(url, { width: mid, quality });
  return { src, srcSet: set };
}
